import React, { useState, useCallback, useMemo } from 'react';
import DesktopLayout from './components/DesktopLayout';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingSpinner from './components/common/LoadingSpinner';
import dataService from './services/dataService';
import censusApiService from './services/censusApiService';
import { debounce } from './utils/performanceUtils';
import { APP_CONFIG } from './constants/appConfig';

function App() {
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [currentStateData, setCurrentStateData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Format census data to match the expected structure
  const formatCensusData = (censusData) => {
    const formatted = {};
    censusData.forEach(item => {
      if (item.state) {
        // Convert state FIPS code to state abbreviation if needed
        const stateAbbr = dataService.getStateAbbreviation(item.state) || item.state;
        formatted[stateAbbr] = parseFloat(item.value) || item.value;
      }
    });
    return formatted;
  };

  // Unified debounced metric change handler
  const debouncedMetricChange = useMemo(
    () => debounce(async (metricData) => {
      // Handle both string (legacy) and object (new unified) metric formats
      const metricKey = typeof metricData === 'string' ? metricData : metricData.key;
      
      if (selectedMetric === metricKey) return;
      
      setLoading(true);
      setError(null);
      setSelectedMetric(metricKey);
      
      try {
        let data;
        
        // Check if metric is from census or custom source
        if (typeof metricData === 'object' && metricData.source === 'census') {
          // Census data is already provided in the metric object
          if (metricData.data) {
            data = formatCensusData(metricData.data);
          } else {
            // Fallback: fetch census data if not provided
            const censusResult = await censusApiService.getStateData('acs5', 2022, [metricData.code]);
            data = formatCensusData(censusResult);
          }
        } else {
          // Use existing dataService for custom metrics
          const metricToFetch = typeof metricData === 'string' ? metricData : metricData.key;
          data = await dataService.getData(metricToFetch);
        }
        
        setCurrentStateData(data);
      } catch (err) {
        console.error('Error loading data:', err);
        const description = typeof metricData === 'object' 
          ? metricData.description 
          : dataService.getDataDescription(metricData);
        setError(`Failed to load ${description}: ${err.message}`);
        setCurrentStateData({});
      } finally {
        setLoading(false);
      }
    }, 300),
    [selectedMetric]
  );

  const handleMetricChange = useCallback((metricData) => {
    debouncedMetricChange(metricData);
  }, [debouncedMetricChange]);

  const handleStateClick = useCallback((stateCode) => {
    setSelectedState(selectedState === stateCode ? null : stateCode);
  }, [selectedState]);

  // Show loading spinner for initial app load
  if (loading && !selectedMetric) {
    return (
      <LoadingSpinner 
        fullScreen={true}
        size="large"
        message={`Loading ${APP_CONFIG.name}...`}
      />
    );
  }

  return (
    <ErrorBoundary>
      <DesktopLayout
        selectedMetric={selectedMetric}
        onMetricChange={handleMetricChange}
        stateData={currentStateData}
        selectedState={selectedState}
        onStateClick={handleStateClick}
        loading={loading}
        error={error}
      />
    </ErrorBoundary>
  );
}

export default App;